"use client";
/**
 * Games' binding of the shared @owlcat/app-shell <AppSidebar>: the hub + one row
 * per game (from the single games catalog), admin for platform admins, and the
 * fleet footer — [top-up pill · profile / language / theme] → support → legal.
 * All look + drawer behaviour lives in the package, so games matches habits,
 * stories and film.
 */
import { useTranslations } from "next-intl";
import { LayoutGrid, Shield, ShieldCheck } from "lucide-react";
import {
  AppSidebar,
  AppSidebarFooter,
  AppHeaderControls,
  type SidebarNavItem,
  type AppHeaderControlsProps,
} from "@owlcat/app-shell";
import { Link, usePathname } from "@/lib/i18n/navigation";
import { gamesCatalog } from "@/lib/games-catalog";
import { GamesTopUpPill } from "./GamesTopUpPill";

export function GamesSidebar({
  brand,
  chrome,
  isAdmin = false,
  signedIn = false,
  legal,
  mobileBar = true,
}: {
  brand: { label: string; emoji: string; href: string };
  chrome: AppHeaderControlsProps;
  isAdmin?: boolean;
  signedIn?: boolean;
  /** accounts /info/privacy + /info/terms with this app's return — the fleet's legal row */
  legal: { privacyUrl: string; termsUrl: string };
  /** false on game pages: the game header is the only bar on a phone (desktop rail stays). */
  mobileBar?: boolean;
}) {
  const t = useTranslations();
  const pathname = usePathname(); // locale-stripped → matches the locale-relative hrefs

  const items: SidebarNavItem[] = [
    { id: "hub", label: t("nav.hub"), href: "/", icon: <LayoutGrid className="h-5 w-5" /> },
    ...gamesCatalog.map((g) => {
      const Icon = g.icon;
      return {
        id: g.id,
        label: t(g.name),
        href: g.href,
        icon: <Icon className="h-5 w-5" />,
        badge: g.isNew ? t("games.new") : undefined,
      } satisfies SidebarNavItem;
    }),
  ];
  if (isAdmin) {
    items.push({ id: "admin", label: t("nav.admin"), href: "/admin", icon: <Shield className="h-4 w-4" />, secondary: true });
    // the fleet admin (accounts: users, billing, subscriptions, comps) — the same entry in every app
    items.push({ id: "accounts-admin", label: t("nav.accountsAdmin"), href: `${(chrome.profile.accountsUrl ?? "").replace(/\/+$/, "")}/admin`, icon: <ShieldCheck className="h-4 w-4" />, secondary: true });
  }

  const footer = (close: () => void) => (
    <AppSidebarFooter
      // Games has no plan tier: the "subscription" slot shows what the allowance
      // BUYS ("5 лабиринтов из фото") + the top-up entry — the ONE always-visible
      // way to top up, on every page.
      subscription={signedIn ? <GamesTopUpPill /> : null}
      profile={<AppHeaderControls {...chrome} />}
      support={{ label: t("nav.support") }}
      legal={{
        privacyUrl: legal.privacyUrl,
        termsUrl: legal.termsUrl,
        labels: { privacy: t("nav.privacy"), cookies: t("nav.cookies"), terms: t("nav.terms") },
      }}
      onNavigate={close}
    />
  );

  return (
    <AppSidebar brand={brand} items={items} activePath={pathname} linkComponent={Link} footer={footer} collapsedFooter={<AppHeaderControls {...chrome} compact />} menuLabel={t("nav.menu")} mobileBar={mobileBar} />
  );
}
